import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/Button";
import { EmptyState, ErrorState, Skeleton } from "../components/ui/Feedback";
import { ProductFilters } from "../features/products/ProductFilters";
import { ProductsTable } from "../features/products/ProductsTable";
import { useProducts } from "../features/products/useProducts";
import { formatNumber } from "../lib/format";

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 250;

export const ProductsPage = () => {
	const [params, setParams] = useSearchParams();
	const status = params.get("status") ?? "";
	const page = Math.max(0, Number(params.get("page") ?? 0));

	const [searchInput, setSearchInput] = useState(params.get("q") ?? "");
	const [search, setSearch] = useState(searchInput);

	useEffect(() => {
		const timer = setTimeout(() => setSearch(searchInput), SEARCH_DEBOUNCE_MS);
		return () => clearTimeout(timer);
	}, [searchInput]);

	const updateParams = (next: Record<string, string>) => {
		const merged = new URLSearchParams(params);
		for (const [key, value] of Object.entries(next)) {
			if (value) merged.set(key, value);
			else merged.delete(key);
		}
		setParams(merged, { replace: true });
	};

	useEffect(() => {
		updateParams({ q: search, page: "" });
		// eslint-disable-next-line react-hooks/exhaustive-deps -- updateParams is rebuilt every render; only a new search term should write the url
	}, [search]);

	const query = useProducts({ search, status, limit: PAGE_SIZE, offset: page * PAGE_SIZE });
	const total = query.data?.total ?? 0;
	const lastPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);

	return (
		<div className="space-y-6">
			<div className="flex flex-wrap items-end justify-between gap-4">
				<div>
					<h1 className="text-xl font-semibold tracking-tight text-ink">Products</h1>
					<p className="mt-1 text-sm text-ink-muted">
						{query.isPending ? "Loading…" : `${formatNumber(total)} products`}
					</p>
				</div>
				<ProductFilters
					search={searchInput}
					status={status}
					onSearchChange={setSearchInput}
					onStatusChange={(value) => updateParams({ status: value, page: "" })}
				/>
			</div>

			{query.isError ? (
				<ErrorState
					title="Could not load products"
					description={query.error.message}
					action={
						<Button variant="outline" size="sm" onClick={() => query.refetch()}>
							Try again
						</Button>
					}
				/>
			) : null}

			{query.isPending ? (
				<div className="space-y-2">
					{Array.from({ length: 6 }, (_, index) => (
						<Skeleton key={index} className="h-14 w-full" />
					))}
				</div>
			) : null}

			{query.data && query.data.items.length === 0 ? (
				<EmptyState
					title="No products match"
					description={
						search || status
							? "Try clearing the search or status filter."
							: "Run a sync to pull your catalog from Shopify."
					}
				/>
			) : null}

			{query.data && query.data.items.length > 0 ? (
				<>
					<div className="overflow-hidden rounded-md border border-line">
						<ProductsTable products={query.data.items} />
					</div>
					<div className="flex items-center justify-between text-xs text-ink-muted">
						<span>
							Page {page + 1} of {lastPage + 1}
						</span>
						<div className="flex gap-2">
							<Button
								variant="outline"
								size="sm"
								disabled={page === 0}
								onClick={() => updateParams({ page: String(page - 1) })}
							>
								Previous
							</Button>
							<Button
								variant="outline"
								size="sm"
								disabled={page >= lastPage}
								onClick={() => updateParams({ page: String(page + 1) })}
							>
								Next
							</Button>
						</div>
					</div>
				</>
			) : null}
		</div>
	);
};
